import {
  BaseElement,
  ContainerElement,
  ContainerLayout,
  containerLayout,
  ElementVisibility,
  MarketingForgeElementType,
} from "@redotech/redo-content-builder-common/element-schema";
import { Flex } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { memo, ReactNode } from "react";
import { getBaseElementStyles } from "./base-element-styles";
import { SignupFormDiscountCodeDisplay } from "./discount-code";
import { FormButton, shouldHideFormButton } from "./form-button";
import { FormImage } from "./form-image";
import { FormProduct } from "./form-product";
import { FormElementProps } from "./index";
import { Input } from "./input";
import { RichText } from "./rich-text";
import { TextToSubscribeButton } from "./text-to-subscribe-button";
import { TextToSubscribeDescription } from "./text-to-subscribe-description";

type ContainerStyleProps = Pick<
  BaseElement,
  "backgroundColor" | "borderColor" | "borderWidth" | "padding"
> &
  Pick<ContainerElement, "gapPx" | "justify">;

export const ContainerStyleWrapper = memo(function ContainerStyleWrapper({
  element,
  layout,
  children,
}: {
  element: ContainerStyleProps;
  layout: ContainerLayout;
  children: ReactNode;
}) {
  return (
    <Flex
      align="stretch"
      dir={layout.direction}
      flex="1"
      gap="none"
      style={{
        ...getBaseElementStyles(element),
        ...(element.gapPx !== undefined && { gap: `${element.gapPx}px` }),
        justifyContent: layout.justifyContent,
      }}
    >
      {children}
    </Flex>
  );
});

interface ContainerChildWrapperProps {
  direction: "row" | "column";
  children: ReactNode;
}

export const ContainerChildWrapper = memo(function ContainerChildWrapper({
  direction,
  children,
}: ContainerChildWrapperProps) {
  const isHorizontal = direction === "row";
  return (
    <Flex
      align="stretch"
      dir={isHorizontal ? "row" : "column"}
      flex={isHorizontal ? "1" : undefined}
      gap="none"
    >
      {children}
    </Flex>
  );
});

export const FormContainer = memo(function FormContainer({
  element,
  isMobile = false,
  ...props
}: FormElementProps<ContainerElement> & { isMobile?: boolean }) {
  const layout = containerLayout(element, isMobile);
  const direction = layout.direction;

  return (
    <ContainerStyleWrapper element={element} layout={layout}>
      {element.elements
        .filter((childElement) => {
          const visibility = childElement.visibility ?? ElementVisibility.ALL;
          if (visibility === ElementVisibility.DESKTOP_ONLY && isMobile) {
            return false;
          }
          if (visibility === ElementVisibility.MOBILE_ONLY && !isMobile) {
            return false;
          }
          // Skip close buttons that FormButton would render as null in this
          // context (no onClose wired, e.g. embedded mode). Wrapping them in
          // ContainerChildWrapper anyway leaves a 0-height phantom row.
          if (
            childElement.type === MarketingForgeElementType.BUTTON &&
            shouldHideFormButton(childElement, props.onClose, props.isBuilder)
          ) {
            return false;
          }
          return true;
        })
        .map((childElement, index) => (
          <ContainerChildWrapper
            direction={direction}
            key={childElement._id ?? index}
          >
            <FormContainerChild
              element={childElement}
              isMobile={isMobile}
              {...props}
            />
          </ContainerChildWrapper>
        ))}
    </ContainerStyleWrapper>
  );
});

const FormContainerChild = memo(function FormContainerChild({
  element,
  isMobile,
  ...props
}: { element: ContainerElement["elements"][number]; isMobile: boolean } & Omit<
  FormElementProps<ContainerElement>,
  "element"
>) {
  const type = element.type;
  switch (type) {
    case MarketingForgeElementType.RICH_TEXT:
      return <RichText element={element} {...props} />;
    case MarketingForgeElementType.IMAGE:
      return <FormImage element={element} {...props} />;
    case MarketingForgeElementType.PRODUCT:
      return <FormProduct element={element} {...props} />;
    case MarketingForgeElementType.CONTAINER:
      return <FormContainer element={element} isMobile={isMobile} {...props} />;
    case MarketingForgeElementType.INPUT:
      return <Input element={element} {...props} />;
    case MarketingForgeElementType.BUTTON:
      return <FormButton element={element} {...props} />;
    case MarketingForgeElementType.DISCOUNT:
      return <SignupFormDiscountCodeDisplay element={element} {...props} />;
    case MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON:
      return <TextToSubscribeButton element={element} {...props} />;
    case MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION:
      return <TextToSubscribeDescription element={element} {...props} />;
    case MarketingForgeElementType.COLUMN:
      throw new Error("Column type not supported");
    default:
      assertNever(type);
  }
});
